import { Loader2 } from 'lucide-react';
import { formatApiError, moduleStaleSummary, moduleStatusHint } from '@mustauto/utils';
import { Card, CardContent, CardHeader, CardTitle, ErrorState, Skeleton, useToast } from '@mustauto/ui/web';
import { useWorkshopModules } from '@/hooks/useSettings';
import { ModuleRadioGroup } from '@/components/ModuleRadioGroup';

/**
 * Configurações → Perfil → "Tipos de serviço": liga e desliga os módulos da
 * oficina (mecânica, funilaria, estética) e escolhe qual vem marcado ao abrir
 * uma OS. É a mesma seção do perfil no app do gestor, com os mesmos textos
 * (`moduleStatusHint`, `moduleStaleSummary` em @mustauto/utils).
 *
 * Diferente do resto do perfil, cada mudança salva na hora — e o cartão diz
 * isso, porque fica logo abaixo de um formulário com botão Salvar. Muda o fluxo
 * de abertura da oficina inteira, e o gestor precisa saber na hora se valeu
 * (outro gestor pode ter desligado o módulo no mesmo instante: a API responde
 * 400, e o cartão relê o estado real).
 */
export function WorkshopModulesCard({ readOnly }: { readOnly: boolean }) {
  const { toast } = useToast();
  const { data, isLoading, isError, error, refetch, setEnabled, setDefault, saving } = useWorkshopModules();

  const run = async (write: () => Promise<unknown>) => {
    try {
      await write();
    } catch (err) {
      toast({
        type: 'error',
        title: 'Não foi possível salvar',
        description: formatApiError(err, 'Tente novamente em instantes.'),
      });
    }
  };

  const busy = saving !== null || readOnly;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Tipos de serviço</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <div className="space-y-2" aria-busy="true">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : isError || !data ? (
          <ErrorState error={error} onRetry={() => refetch()} />
        ) : (
          <>
            <p className="text-xs text-muted-foreground">
              Com mais de um ligado, quem abre a OS escolhe o tipo. Cada tipo tem as próprias etapas e o
              próprio tempo de &quot;OS parada&quot; ({moduleStaleSummary(data.modules)}); o tempo escolhido
              acima vale para as OS sem tipo. As mudanças aqui valem na hora, sem o botão Salvar.
            </p>

            <div className="divide-y rounded-lg border border-input">
              {data.modules.map((m) => (
                <label
                  key={m.module}
                  className={`flex items-start gap-3 p-4 ${m.locked || busy ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <input
                    type="checkbox"
                    checked={m.enabled}
                    disabled={m.locked || busy}
                    onChange={(e) => void run(() => setEnabled({ module: m.module, enabled: e.target.checked }))}
                    className="mt-0.5 rounded border-input"
                  />
                  <span className="flex-1">
                    <span className="block text-sm font-medium">{m.label}</span>
                    <span className="block text-xs text-muted-foreground">{moduleStatusHint(m)}</span>
                  </span>
                  {saving === m.module && <Loader2 size={14} className="animate-spin text-muted-foreground" />}
                </label>
              ))}
            </div>

            {(() => {
              const enabled = data.modules.filter((m) => m.enabled).map((m) => m.module);
              if (enabled.length < 2) return null;
              const def = data.defaultModule;
              return (
                <div className="flex flex-col gap-2">
                  <span className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                    Tipo já marcado ao abrir uma OS
                    {saving === 'default' && <Loader2 size={12} className="animate-spin" />}
                  </span>
                  <ModuleRadioGroup
                    name="defaultModule"
                    label="Tipo já marcado ao abrir uma OS"
                    options={enabled}
                    value={def.module}
                    suggested={def.suggested}
                    disabled={busy}
                    onChange={(module) => {
                      if (module !== def.module) void run(() => setDefault(module));
                    }}
                  />
                </div>
              );
            })()}

            {readOnly && (
              <p className="text-xs text-muted-foreground">Só o gestor da oficina muda os tipos de serviço.</p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
